import { useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { Box, IconButton, SvgIcon, CircularProgress } from "@mui/material";
import { toast } from "react-hot-toast";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronRight, faChevronDown } from "@fortawesome/pro-solid-svg-icons";
import useAssetTreeStore from "@stores/client/useAssetTreeStore";
import { getNodeIcon } from "@utils/shared/get-node-icon";
import { getIconByName } from "@utils/client/get-icon-by-name";

// Resolve a node's icon exactly like the admin tree and the client tile views:
// use the custom icon when the item specifies one, otherwise fall back to the
// type-based default (double folder for areas, folder for processes, file for
// leaves). Folders swap to their solid/open variant while the node is expanded.
const resolveIcon = (item, isExpanded) =>
  getIconByName(item.icon) ?? getNodeIcon(item.type, isExpanded);

const itemShape = PropTypes.shape({
  type: PropTypes.number,
  icon: PropTypes.string,
  path: PropTypes.string.isRequired,
  hasChildren: PropTypes.bool.isRequired,
});

// The type/custom icon, always shown (mirrors the admin's leading node icon).
const NodeIcon = ({ item, isExpanded, isSelected }) => (
  <SvgIcon data-testid="tree-item-icon" sx={{ fontSize: 14, color: isSelected ? "primary.dark" : "text.primary" }}>
    <FontAwesomeIcon icon={resolveIcon(item, isExpanded)} />
  </SvgIcon>
);
NodeIcon.propTypes = {
  item: itemShape.isRequired,
  isExpanded: PropTypes.bool.isRequired,
  isSelected: PropTypes.bool.isRequired,
};

// Leading chevron — clickable expand/collapse; fetches children on first open
// and shows a spinner while loading. Matches the admin tree's leading chevron.
const ExpandToggle = ({ item, isExpanded, onToggle }) => {
  const { getAssetTreeItem, setNodesOpen } = useAssetTreeStore();
  const [fetchingChildren, setFetchingChildren] = useState(false);
  const { t } = useTranslation();

  const handleToggle = async (e) => {
    e.stopPropagation();
    if (isExpanded) {
      onToggle();
      setNodesOpen(item.path, false);
      return;
    }
    // Remove the cache check — always fetch fresh data when expanding a node
    setFetchingChildren(true);
    try {
      await getAssetTreeItem(item.path);
      setFetchingChildren(false);
      onToggle();
      setNodesOpen(item.path, true);
    } catch (error) {
      console.error("Failed to fetch asset tree children:", error);
      setFetchingChildren(false);
      toast.error(t("assets.fetchChildrenError"));
    }
  };

  return (
    <IconButton
      size="small"
      onClick={handleToggle}
      sx={{ width: 20, height: 20, p: 0, flexShrink: 0 }}
      aria-label={isExpanded ? t("common.collapse") : t("common.expand")}
    >
      {fetchingChildren ? (
        <CircularProgress
          data-testid="circular-progress"
          size={11}
          sx={{ color: "inherit" }}
        />
      ) : (
        <SvgIcon
          data-testid={isExpanded ? "chevron-down" : "chevron-right"}
          sx={{ fontSize: 11 }}
        >
          <FontAwesomeIcon icon={isExpanded ? faChevronDown : faChevronRight} />
        </SvgIcon>
      )}
    </IconButton>
  );
};
ExpandToggle.propTypes = {
  item: itemShape.isRequired,
  isExpanded: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
};

export const TreeItemIcon = ({ item, onToggle, isExpanded, isSelected }) => (
  <>
    {item.hasChildren ? (
      <ExpandToggle item={item} isExpanded={isExpanded} onToggle={onToggle} />
    ) : (
      // Blank spacer of the chevron's size so leaf icons/titles stay aligned.
      <Box aria-hidden="true" sx={{ width: 20, height: 20, flexShrink: 0 }} />
    )}
    <NodeIcon item={item} isExpanded={isExpanded} isSelected={isSelected} />
  </>
);

TreeItemIcon.propTypes = {
  item: itemShape.isRequired,
  onToggle: PropTypes.func.isRequired,
  isExpanded: PropTypes.bool.isRequired,
  isSelected: PropTypes.bool.isRequired,
};
